import React, { useEffect, useState } from 'react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { fetchPolicy, savePolicy } from '../../Redux/Reducers/PolicyReducer';
import {
    Box,
    Typography,
    Button,
    Paper,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    GlobalStyles
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const PrivacyPolicy = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { policies, loading } = useSelector((state) => state.PolicyReducer);

    const [dataEn, setDataEn] = useState("");
    const [dataFr, setDataFr] = useState("");
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [notifyUser, setNotifyUser] = useState(true);

    const type = "privacy-policy";

    /* ================= FETCH ================= */
    useEffect(() => {
        dispatch(fetchPolicy(type));
    }, [dispatch]);

    useEffect(() => {
        if (policies[type]) {
            setDataEn(policies[type]?.en?.content || "");
            setDataFr(policies[type]?.fr?.content || "");
        }
    }, [policies]);

    /* ================= SAVE ================= */
    const handleSave = async () => {
        try {
            await dispatch(
                savePolicy({
                    type,
                    notify_user: notifyUser ? "true" : "false",
                    en: {
                        title: "Privacy Policy",
                        content: dataEn
                    },
                    fr: {
                        title: "Politique de Confidentialité",
                        content: dataFr
                    }
                })
            ).unwrap();

            toast.success(
                t('privacy_policy_saved', 'Privacy Policy saved successfully!')
            );
        } catch (err) {
            toast.error(
                t('privacy_policy_save_failed', 'Failed to save privacy policy')
            );
        } finally {
            setSaveDialogOpen(false);
        }
    };

    return (
        <Box sx={{ p: '36px 24px 24px' }}>
            {/* ===== HEADER ===== */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'> {t('privacy_policy', 'Privacy Policy')} </Typography>
                <Button
                    variant="contained"
                    onClick={() => setSaveDialogOpen(true)}
                    disabled={loading}
                    sx={{ 
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                        color: '#fff',
                        textTransform: 'none',
                        backgroundColor: '#FF69B4 !important',
                        borderRadius: '999px',
                        fontSize: '15px',
                        lineHeight: '24px',
                        padding: '6px 20px',
                        minWidth: '38px',
                        '& .MuiButton-startIcon': {
                        margin: 0,
                        },
                    }}
                >
                    {loading ? t('saving', 'Saving...') : t('save_changes', 'Save Changes')}
                </Button>
            </Box>

            {/* ===== EDITORS ===== */}
            <Grid container spacing={3}>
                {/* ===== ENGLISH ===== */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'white', fontSize: '18px', fontWeight: 500 }}> {t('english', 'English')} </Typography>
                    <Paper 
                        sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "none !important",
                            backgroundColor: "#252525 !important",
                            borderRadius: '20px !important',
                            boxShadow: 'none !important',
                            mb: 2,
                            "& .MuiPaper-root": {
                                borderRadius: '0 !important',
                            },
                            "& .ck.ck-editor": {
                                backgroundColor: "#252525",
                            },
                            "& .ck.ck-toolbar": {
                                backgroundColor: "#252525 !important",
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                borderRadius: "20px 20px 0 0 !important"
                            },
                            "& .ck.ck-toolbar *": {
                                color: "#ffffff !important",
                            },
                            "& .ck.ck-editor__editable": {
                                backgroundColor: "#252525 !important",
                                color: "#ffffff !important",
                                minHeight: "300px",
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "none !important",
                                borderRadius: '0 0 20px 20px !important',
                                fontSize: "14px !important"
                            },
                            "& .ck.ck-editor__main .ck-editor__editable:not(.ck-focused)": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                            },
                            "& .ck.ck-editor__editable::placeholder": {
                                color: "rgba(255,255,255,0.5)",
                            },
                            "& .ck.ck-editor__editable.ck-focused": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "0 0 0 1px rgba(230, 210, 249, 0.2) inset",
                            },
                            "& .ck.ck-button:hover": {
                                backgroundColor: "rgba(255,255,255,0.08) !important",
                            },
                            "& .ck.ck-button.ck-on": {
                                backgroundColor: "rgba(230, 210, 249, 0.2) !important",
                            },
                            "& .ck.ck-dropdown__panel": {
                                backgroundColor: "#1a1a1a !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                                boxShadow: "0 8px 20px rgba(0,0,0,0.6)",
                            },
                            "& .ck.ck-list": {
                                backgroundColor: "#1a1a1a !important",
                            },
                            "& .ck.ck-list__item .ck-button": {
                                color: "#ffffff !important",
                            },
                            "& .ck.ck-list__item .ck-button:hover": {
                                backgroundColor: "rgba(255,255,255,0.08) !important",
                            },
                            "& .ck.ck-list__item .ck-button.ck-on": {
                                backgroundColor: "rgba(230, 210, 249, 0.2) !important",
                                color: "#000 !important",
                            },
                            "& .ck.ck-balloon-panel": {
                                backgroundColor: "#1a1a1a !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                                boxShadow: "0 8px 20px rgba(0,0,0,0.6)",
                            },
                            "& .ck.ck-input-text": {
                                backgroundColor: "#121212 !important",
                                color: "#ffffff !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                            },
                            "& .ck.ck-input-text:focus": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "none",
                            },
                        }}
                    >
                        <CKEditor
                            editor={ClassicEditor}
                            data={dataEn}
                            config={{
                                toolbar: [
                                    "heading", "|",
                                    "bold", "italic", "link",
                                    "bulletedList", "numberedList", "|",
                                    "blockQuote", "insertTable"
                                ],
                                placeholder: t(
                                    'enter_privacy_policy_en',
                                    'Enter Privacy Policy in English...'
                                )
                            }}
                            onChange={(event, editor) =>
                                setDataEn(editor.getData())
                            }
                        />
                    </Paper>
                </Grid>

                {/* ===== FRENCH ===== */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 1, color: 'white', fontSize: '18px', fontWeight: 500 }}> {t('french', 'French')} </Typography>
                    <Paper 
                        sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "none !important",
                            backgroundColor: "#252525 !important",
                            borderRadius: '20px !important',
                            boxShadow: 'none !important',
                            mb: 2,
                            "& .MuiPaper-root": {
                                borderRadius: '0 !important',
                            },
                            "& .ck.ck-editor": {
                                backgroundColor: "#252525",
                            },
                            "& .ck.ck-toolbar": {
                                backgroundColor: "#252525 !important",
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                borderRadius: "20px 20px 0 0 !important"
                            },
                            "& .ck.ck-toolbar *": {
                                color: "#ffffff !important",
                            },
                            "& .ck.ck-editor__editable": {
                                backgroundColor: "#252525 !important",
                                color: "#ffffff !important",
                                minHeight: "300px",
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "none !important",
                                borderRadius: '0 0 20px 20px !important',
                                fontSize: "14px !important"
                            },
                            "& .ck.ck-editor__main .ck-editor__editable:not(.ck-focused)": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                            },
                            "& .ck.ck-editor__editable::placeholder": {
                                color: "rgba(255,255,255,0.5)",
                            },
                            "& .ck.ck-editor__editable.ck-focused": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "0 0 0 1px rgba(230, 210, 249, 0.2) inset",
                            },
                            "& .ck.ck-button:hover": {
                                backgroundColor: "rgba(255,255,255,0.08) !important",
                            },
                            "& .ck.ck-button.ck-on": {
                                backgroundColor: "rgba(230, 210, 249, 0.2) !important",
                            },
                            "& .ck.ck-dropdown__panel": {
                                backgroundColor: "#1a1a1a !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                                boxShadow: "0 8px 20px rgba(0,0,0,0.6)",
                            },
                            "& .ck.ck-list": {
                                backgroundColor: "#1a1a1a !important",
                            },
                            "& .ck.ck-list__item .ck-button": {
                                color: "#ffffff !important",
                            },
                            "& .ck.ck-list__item .ck-button:hover": {
                                backgroundColor: "rgba(255,255,255,0.08) !important",
                            },
                            "& .ck.ck-list__item .ck-button.ck-on": {
                                backgroundColor: "rgba(230, 210, 249, 0.2) !important",
                                color: "#000 !important",
                            },
                            "& .ck.ck-balloon-panel": {
                                backgroundColor: "#1a1a1a !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                                boxShadow: "0 8px 20px rgba(0,0,0,0.6)",
                            },
                            "& .ck.ck-input-text": {
                                backgroundColor: "#121212 !important",
                                color: "#ffffff !important",
                                border: "1px solid rgba(230, 210, 249, 0.2)",
                            },
                            "& .ck.ck-input-text:focus": {
                                borderColor: "rgba(230, 210, 249, 0.2) !important",
                                boxShadow: "none",
                            },
                        }}
                    >
                        <CKEditor
                            editor={ClassicEditor}
                            data={dataFr}
                            config={{
                                toolbar: [
                                    "heading", "|",
                                    "bold", "italic", "link",
                                    "bulletedList", "numberedList", "|",
                                    "blockQuote", "insertTable"
                                ],
                                placeholder: t(
                                    'enter_privacy_policy_fr',
                                    'Entrez la politique de confidentialité en français...'
                                )
                            }}
                            onChange={(event, editor) =>
                                setDataFr(editor.getData())
                            }
                        />
                    </Paper>
                </Grid>
            </Grid>

            <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)}>
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>
                    {t('confirm_save', 'Confirm Save')}
                </DialogTitle>
                <DialogContent sx={{ pb: '0', minWidth: { sm: '420px' } }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center', mb: 2 }}>
                        {t('privacy_policy_save_confirmation', 'Are you sure you want to save Privacy Policy?')}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={notifyUser}
                                    onChange={(e) => setNotifyUser(e.target.checked)}
                                    sx={{
                                        color: 'rgba(255,255,255,0.7)',
                                        '&.Mui-checked': {
                                            color: '#FF69B4',
                                        },
                                    }}
                                />
                            }
                            label={t('send_notification', 'Send notification')}
                            sx={{
                                mx: 0,
                                '& .MuiFormControlLabel-label': {
                                    color: '#fff',
                                    fontSize: '15px',
                                },
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                    <Button
                        onClick={() => setSaveDialogOpen(false)}
                        sx={{
                            backgroundColor: 'rgba(227, 214, 245, 0.2) !important',
                            border: '1px solid rgba(227, 214, 245, 0.1)',
                            fontSize: '15px',
                            lineHeight: '20px',
                            fontWeight: '400',
                            padding: '11px 20px',
                            minWidth: { xs: '100%', sm: '150px' },
                            color: 'white',
                            borderRadius: '999px',
                            textTransform: 'capitalize',
                        }}
                    >
                        {t('no', 'No')}
                    </Button>
                    <Button
                        variant="contained"
                        disabled={loading}
                        onClick={handleSave}
                        sx={{
                            py: 1.5,
                            borderRadius: '999px',
                            fontWeight: '400',
                            fontSize: '15px',
                            lineHeight: '20px',
                            textTransform: 'capitalize',
                            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            minWidth: { xs: '100%', sm: '150px' },
                            marginLeft: '10px',
                        }}
                    >
                        {loading ? <CircularProgress size={20} /> : t('yes', 'Yes')}
                    </Button>
                </DialogActions>
            </Dialog>

            <GlobalStyles
                styles={{
                    '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
                        backgroundColor: '#252525 !important',
                        border: '1px solid rgba(230, 210, 249, 0.1)',
                        fontSize: '15px',
                        color: '#fff',
                        borderRadius: '4px',
                    },
                    '.MuiDialog-root .MuiPaper-root': {
                        borderRadius: '20px',
                    },
                    '.MuiDataGrid-columnsManagementHeader': {
                        padding: '10px !important',
                    },
                    '.MuiInputBase-formControl': {
                        fontSize: '14px !important',
                        color: 'white !important',
                        borderRadius: '999px !important',
                    },
                    '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
                        borderTop: '1px solid rgba(230, 210, 249, 0.1)',
                        padding: '4px 8px 4px 16px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement':{
                        padding: '0px 16px 12px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
                        color: '#FF69B4',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
                        color: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
                        borderColor: 'rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
                        display: 'none',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
                        padding: '0',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
                        padding: '5px 10px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: '#FF69B4',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
                        borderColor: 'rgba(230, 210, 249, 0.3) !important',
                        margin: '0',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        minWidth: '32px',
                    },
                }}
            />
        </Box>
    );
};

export default PrivacyPolicy;
